import { useState, useRef } from 'react';
import { compressImageFile } from '../../utils/imageUtils';
import PhotoPickerModal from '../PhotoPickerModal';

const MAX_FILES = 10;
const ACCEPTED_TYPES = 'image/*,.csv,.pdf,.doc,.docx,.xls,.xlsx,.txt';

export default function StepPhoto({ data, update, onBack, onFinish, submitting }) {
  const fileRef = useRef(null);
  const [showPhotoPicker, setShowPhotoPicker] = useState(false);

  const files = data.receiptPhotos || [];
  const hasFiles = files.length > 0;

  async function handleFiles(e) {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;
    // Reset input so same file can be re-selected later
    if (fileRef.current) fileRef.current.value = '';

    const newFiles = [];
    for (const file of selected) {
      if (files.length + newFiles.length >= MAX_FILES) break;

      const isImage = file.type.startsWith('image/');
      if (isImage) {
        // Read and compress images
        const compressed = await compressImageFile(file, 1200, 0.8);
        newFiles.push({ type: 'image', name: file.name, data: compressed });
      } else {
        // Non-image files — store name + data URL for CSV/text extraction
        const dataUrl = await readFileAsDataURL(file);
        newFiles.push({ type: 'file', name: file.name, mimeType: file.type, data: dataUrl });
      }
    }

    update({ receiptPhotos: [...files, ...newFiles] });
  }

  function removeFile(index) {
    const updated = files.filter((_, i) => i !== index);
    update({ receiptPhotos: updated.length ? updated : [] });
  }

  function openFilePicker() {
    // Remove capture attribute for file browsing and reset value
    if (fileRef.current) {
      fileRef.current.removeAttribute('capture');
      fileRef.current.value = '';
      fileRef.current.click();
    }
  }

  return (
    <>
      <div className="screen-header">
        <h1 className="screen-title">Service Records</h1>
        <p className="screen-subtitle">
          Upload photos of service receipts, maintenance records, or CSV files.
          We&apos;ll scan them and extract the details.
        </p>
      </div>

      <div style={{ flex: 1, paddingTop: 24 }}>
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPTED_TYPES}
          multiple
          onChange={handleFiles}
          style={{ display: 'none' }}
        />

        {/* Upload zone — always visible when under max */}
        {files.length < MAX_FILES && (
          <button type="button" onClick={() => setShowPhotoPicker(true)} style={uploadZoneStyle}>
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--text-muted)"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.9375rem' }}>
              {hasFiles ? 'Add more files' : 'Tap to upload'}
            </span>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
              Photos, CSV, PDF, or text files
            </span>
            {hasFiles && (
              <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                {files.length} / {MAX_FILES} files
              </span>
            )}
          </button>
        )}

        {/* Uploaded files grid */}
        {hasFiles && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
            {files.map((file, i) => (
              <div key={i} style={fileTileStyle}>
                {file.type === 'image' ? (
                  <img
                    src={file.data}
                    alt={file.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: 'var(--radius-sm)',
                    }}
                  />
                ) : (
                  <div style={fileIconStyle}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    <span style={{ fontSize: '0.5625rem', color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', padding: '0 2px' }}>
                      {file.name}
                    </span>
                  </div>
                )}
                <button
                  onClick={() => removeFile(i)}
                  style={removeBtnStyle}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Service History Details */}
        <div className="form-group" style={{ marginTop: 24 }}>
          <label className="form-label">Additional Service History Details (Optional)</label>
          <textarea
            className="form-input"
            rows={4}
            value={data.serviceHistory || ''}
            onChange={(e) => update({ serviceHistory: e.target.value })}
            placeholder="List any major services, repairs, or modifications..."
            style={{
              resize: 'vertical',
              minHeight: 80,
              fontFamily: 'inherit',
              lineHeight: 1.5,
            }}
          />
        </div>
      </div>

      <div className="nav-row">
        <button className="btn btn-secondary" onClick={onBack} disabled={submitting}>
          Back
        </button>
        {!hasFiles && !data.serviceHistory && (
          <button
            className="btn btn-secondary"
            onClick={onFinish}
            disabled={submitting}
            style={{ flex: 1, opacity: submitting ? 0.6 : 1 }}
          >
            {submitting ? 'Saving...' : 'Skip for Now'}
          </button>
        )}
        <button className="btn btn-primary" onClick={onFinish} disabled={submitting} style={{ flex: 1, opacity: submitting ? 0.6 : 1 }}>
          {submitting ? 'Saving...' : hasFiles || data.serviceHistory ? 'Analyze My Vehicle' : 'Continue'}
        </button>
      </div>

      <PhotoPickerModal
        show={showPhotoPicker}
        onClose={() => setShowPhotoPicker(false)}
        onSelect={(source) => {
          setShowPhotoPicker(false);
          if (fileRef.current) {
            // Reset value before click to ensure onChange fires even for same file
            fileRef.current.value = '';
            fileRef.current.removeAttribute('capture');
            fileRef.current.click();
          }
        }}
      />
    </>
  );
}

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

const uploadZoneStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  padding: '48px 24px',
  border: '2px dashed var(--border)',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  transition: 'border-color 0.2s',
  width: '100%',
};

const fileTileStyle = {
  position: 'relative',
  width: 80,
  height: 80,
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  overflow: 'hidden',
  flexShrink: 0,
};

const fileIconStyle = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
  background: 'var(--bg-elevated)',
};

const removeBtnStyle = {
  position: 'absolute',
  top: 2,
  right: 2,
  width: 22,
  height: 22,
  borderRadius: '50%',
  background: 'rgba(0,0,0,0.7)',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.875rem',
  lineHeight: 1,
  border: 'none',
  cursor: 'pointer',
  padding: 0,
};
